import { motion } from 'framer-motion';
import { 
  CodeBracketIcon, 
  CpuChipIcon, 
  ShieldCheckIcon, 
  CubeTransparentIcon, 
  LockClosedIcon, 
  ArrowsRightLeftIcon, 
  EyeSlashIcon, 
  ArrowPathIcon, 
  ScaleIcon, 
  DocumentDuplicateIcon,
  CommandLineIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import ContactForm from '../components/contact/ContactForm';

const Technology = () => {
  const techAreas = [
    {
      title: "DSL's",
      description: "Domain-Specific Languages for ZK circuit development",
      icon: CodeBracketIcon,
      code: "TECH_001"
    },
    {
      title: "zkVM's stack",
      description: "Virtual Machines optimized for ZK computations",
      icon: CpuChipIcon,
      code: "TECH_002"
    },
    {
      title: "Proving Systems",
      description: "Efficient proof generation and verification systems",
      icon: ShieldCheckIcon,
      code: "TECH_003"
    },
    {
      title: "Full dApp Support",
      description: "End-to-end support for decentralized applications",
      icon: CubeTransparentIcon,
      code: "TECH_004"
    }
  ];

  const useCases = [
    {
      title: "Private Identity",
      description: "Secure and private identity verification without revealing sensitive information",
      icon: LockClosedIcon,
      code: "USE_001"
    },
    {
      title: "Private Transactions",
      description: "Confidential transactions with complete privacy guarantees",
      icon: ArrowsRightLeftIcon,
      code: "USE_002"
    },
    {
      title: "Selective Reveals",
      description: "Control what information to reveal while keeping the rest private",
      icon: EyeSlashIcon,
      code: "USE_003"
    },
    {
      title: "ZK Bridging",
      description: "Secure cross-chain communication with zero-knowledge proofs",
      icon: ArrowPathIcon,
      code: "USE_004"
    },
    {
      title: "ZK Scaling",
      description: "Scaling solutions including rollups with privacy guarantees",
      icon: ScaleIcon,
      code: "USE_005"
    },
    {
      title: "General Purpose ZK",
      description: "Versatile ZK solutions for various use cases",
      icon: DocumentDuplicateIcon,
      code: "USE_006"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <header className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:14px_24px]" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          {/* Header decoration */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [SYSTEM::TECHNOLOGY_ACCESS]
          </div>

          <div className="mb-8">
            <CommandLineIcon className="w-16 h-16 text-indigo-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                End to End ZK Tech Stack
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto font-mono">
              We take cryptography seriously! That is why we incorporated a cohesive model to build and scale privacy and data protection solution.
            </p>
          </div>
        </motion.div>
      </header>

      {/* Tech Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [TECH::CORE_SYSTEMS]
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <area.icon className="w-8 h-8 text-indigo-400" />
                    <div className="font-mono text-xs text-indigo-500/60">{area.code}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 font-mono">{area.title}</h3>
                  <p className="text-slate-400 font-mono">{area.description}</p>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [TECH::USE_CASES]
          </div>

          <div className="text-center mb-16">
            <BeakerIcon className="w-12 h-12 text-indigo-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                ZK Applications
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto font-mono">
              Explore the diverse applications of Zero-Knowledge technology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
                  group-hover:border-indigo-500/40 transition-all duration-300" />
                
                <div className="relative p-8">
                  <div className="flex items-center justify-between mb-6">
                    <useCase.icon className="w-8 h-8 text-indigo-400" />
                    <div className="font-mono text-xs text-indigo-500/60">{useCase.code}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 font-mono">{useCase.title}</h3>
                  <p className="text-slate-400 font-mono">{useCase.description}</p>

                  {/* Corner decorations */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05),transparent_50%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [SYSTEM::CONTACT_FORM]
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 font-mono">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-500">
                Initialize Connection
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto font-mono">
              Send us your details and we'll establish a secure communication channel within 24 hours
            </p>
          </div>

          <ContactForm />

          {/* Footer decoration */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-indigo-500/40">
            [END::CONTACT_FORM]
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Technology;